var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import format from 'string-format';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLighthouseFetch } from '@/hooks/use-lighthouse-fetch';
import { decodeCreateTemplatePushResponse } from '../../../types/lighthouse/decoders/template-push';
import { decodeReadTemplatePushResponse } from '../../../types/lighthouse/decoders/template-push/exec-context-id';
import { decodeUpdateTemplateStagePushResponse } from '../../../types/lighthouse/decoders/template-push/exec-context-id/stage';
export var TEMPLATE_PUSH_PATH = '/template_push';
export function formatTemplatePushPath(contextId) {
    if (contextId === void 0) { contextId = ':contextId'; }
    return "".concat(TEMPLATE_PUSH_PATH, "/").concat(contextId);
}
export function formatTemplatePushStagePath(contextId) {
    if (contextId === void 0) { contextId = ':contextId'; }
    return "".concat(TEMPLATE_PUSH_PATH, "/").concat(contextId, "/stage");
}
export function useTemplatePushExecContext(contextId, _a) {
    var _this = this;
    var _b = _a === void 0 ? {} : _a, refetchInterval = _b.refetchInterval;
    var _c = useLighthouseFetch(), executeFetch = _c.executeFetch, decodeResponse = _c.decodeResponse, handleCommonStatusCodes = _c.handleCommonStatusCodes;
    var t = useTranslation().t;
    var getTemplateById = useCallback(function (context) { return __awaiter(_this, void 0, void 0, function () {
        var response, _a, data, status, translations;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0: return [4 /*yield*/, executeFetch(formatTemplatePushPath(contextId), __assign({}, context))];
                case 1:
                    response = _b.sent();
                    return [4 /*yield*/, decodeResponse(decodeReadTemplatePushResponse, response)];
                case 2:
                    _a = _b.sent(), data = _a.data, status = _a.status;
                    translations = __assign(__assign({}, t('api.template_push.get')), { error: { message: format(t('api.template_push.get.error.message'), contextId) }, not_found: { message: format(t('api.template_push.get.not_found.message'), contextId) } });
                    handleCommonStatusCodes(data, status, translations);
                    if (status === 200) {
                        return [2 /*return*/, data.template_push];
                    }
                    return [2 /*return*/, null];
            }
        });
    }); }, [decodeResponse, executeFetch, handleCommonStatusCodes, t, contextId]);
    return useQuery({
        queryKey: [TEMPLATE_PUSH_PATH, contextId],
        queryFn: getTemplateById,
        refetchInterval: refetchInterval,
    });
}
export function useCreateTemplatePush() {
    var _this = this;
    var t = useTranslation().t;
    var _a = useLighthouseFetch(), executeFetch = _a.executeFetch, decodeResponse = _a.decodeResponse, handleCommonStatusCodes = _a.handleCommonStatusCodes;
    var queryClient = useQueryClient();
    return useCallback(function (pushDetails) { return __awaiter(_this, void 0, void 0, function () {
        var response, _a, status, data;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0: return [4 /*yield*/, executeFetch(TEMPLATE_PUSH_PATH, { method: 'POST', body: JSON.stringify(pushDetails) })];
                case 1:
                    response = _b.sent();
                    return [4 /*yield*/, decodeResponse(decodeCreateTemplatePushResponse, response)];
                case 2:
                    _a = _b.sent(), status = _a.status, data = _a.data;
                    handleCommonStatusCodes(data, status, t('api.template_push.post'));
                    if (status === 201) {
                        queryClient.setQueryData([TEMPLATE_PUSH_PATH, data.template_push.id], data.template_push);
                        return [2 /*return*/, data.template_push];
                    }
                    return [2 /*return*/, null];
            }
        });
    }); }, [executeFetch, decodeResponse, handleCommonStatusCodes, t, queryClient]);
}
export function useAdvanceTemplatePush(_a) {
    var _this = this;
    var _b = _a === void 0 ? {} : _a, notFoundCallback = _b.notFoundCallback;
    var _c = useLighthouseFetch(), executeFetch = _c.executeFetch, decodeResponse = _c.decodeResponse, handleCommonStatusCodes = _c.handleCommonStatusCodes;
    var t = useTranslation().t;
    return useCallback(function (contextId, stageDetails) { return __awaiter(_this, void 0, void 0, function () {
        var response, _a, data, status, translations;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0: return [4 /*yield*/, executeFetch(formatTemplatePushStagePath(contextId), { method: 'PUT', body: JSON.stringify(stageDetails) })];
                case 1:
                    response = _b.sent();
                    return [4 /*yield*/, decodeResponse(decodeUpdateTemplateStagePushResponse, response)];
                case 2:
                    _a = _b.sent(), data = _a.data, status = _a.status;
                    translations = __assign(__assign({}, t('api.template_push.put')), { not_found: { message: format(t('api.template_push.put.not_found.message'), contextId) } });
                    handleCommonStatusCodes(data, status, translations);
                    if (status === 404 && notFoundCallback) {
                        notFoundCallback(status);
                    }
                    return [2 /*return*/, status === 204];
            }
        });
    }); }, [decodeResponse, executeFetch, handleCommonStatusCodes, t, notFoundCallback]);
}
